import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { FaEye, FaDownload, FaFileExcel } from "react-icons/fa";
import * as XLSX from "xlsx";
import "../../../styles/dashboard/shared/KycVerificationManagement.css";

export default function KycVerificationManagement({ 
  role = "orguser", 
  authService,
  apiBaseUrl = process.env.REACT_APP_API_URL
}) {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [verifications, setVerifications] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const verificationsPerPage = 10;
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);

  // Toast
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const KYC_TYPES = [
    { value: "all", label: "All Types" },
    { value: "PAN_VERIFICATION", label: "PAN Verification" },
    { value: "DL_VERIFICATION", label: "Driving License" },
    { value: "RC_VERIFICATION", label: "RC Verification" },
    { value: "COURT_CASE_CHECK", label: "Court Case Check" },
  ];

  // Fetch organizations for admin role
  useEffect(() => {
    if (role === "admin") {
      fetchOrganizations();
    }
  }, [role]);

  // Fetch verifications when dependencies change
  useEffect(() => {
    fetchVerifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, selectedOrgId, currentPage, searchTerm, selectedType, dateFrom, dateTo]);

  const fetchOrganizations = async () => {
    try {
      const endpoint = `${apiBaseUrl}/admin/organizations`;
      const token = authService.getAuthHeader();
      const response = await fetch(`${endpoint}?page=1&limit=100`, { headers: token });
      const data = await response.json();
      if (data.success) {
        setOrganizations(data.data || []);
      }
    } catch (error) {
      console.error("Fetch orgs error:", error);
      showToast("Failed to load organizations", "error");
    }
  };

  const fetchVerifications = async () => {
    setLoading(true);
    try {
      let endpoint;
      let params = new URLSearchParams({
        page: currentPage,
        limit: verificationsPerPage,
        search: searchTerm,
        type: selectedType,
      });

      // Add date range filters
      if (dateFrom) {
        params.append("dateFrom", dateFrom);
      }
      if (dateTo) {
        params.append("dateTo", dateTo);
      }

      // Determine endpoint based on role
      if (role === "superadmin") {
        endpoint = `${apiBaseUrl}/superadmin/kyc-verifications`;
      } else if (role === "admin") {
        endpoint = `${apiBaseUrl}/admin/kyc-verifications`;
        if (selectedOrgId) {
          params.append("orgId", selectedOrgId);
        }
      } else {
        // orguser - always filtered by their organization
        endpoint = `${apiBaseUrl}/orguser/kyc/all`; // Assuming this endpoint exists
      }
      
      const fullUrl = `${endpoint}?${params}`;
      
      const token = authService.getAuthHeader();
      const response = await fetch(fullUrl, { headers: token });
      const data = await response.json();
      
      if (data.success || data.message === 'KYC verifications retrieved successfully') {
        const rawData = data.data || [];
        setVerifications(rawData);
        setTotalRecords(data.pagination?.totalRecords || data.pagination?.total || rawData.length);
      } else {
        showToast(data.error || "Failed to load verifications", "error");
        setVerifications([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("Fetch verifications error:", error);
      showToast("Failed to load verifications", "error");
      setVerifications([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (msg, type = "success") => {
    setToast({ show: true, message: msg, type });
    setTimeout(() => setToast({ show: false }), 3000);
  };

  // Pagination
  const totalPages = Math.ceil(totalRecords / verificationsPerPage);

  const handleViewPdf = async (verification) => {
    try {
      if (!verification.pdfPath) {
        showToast("PDF is still generating. Please try again in a few seconds.", "error");
        return;
      }
      
      let endpoint;
      if (role === "superadmin") {
        endpoint = `${apiBaseUrl}/superadmin/kyc-verifications/${verification.id}/download`;
      } else if (role === "admin") {
        endpoint = `${apiBaseUrl}/admin/kyc-verifications/${verification.id}/download`;
      } else {
        // orguser might have different endpoint format
        endpoint = `${apiBaseUrl}/orguser/kyc/${verification.type}/${verification.id}/download`;
      }
      
      const token = authService.getAuthHeader();
      const response = await fetch(endpoint, { headers: token });
      const data = await response.json();
      
      const url = data?.data?.url || data?.url || data?.downloadUrl || data?.pdfUrl;
      if (url) {
        window.open(url, '_blank');
      } else {
        showToast("PDF not available yet", "error");
      }
    } catch (err) {
      console.error("Failed to view KYC verification PDF", err);
      showToast("Failed to load PDF report. Please try again.", "error");
    }
  };

  const handleDownload = async (verification) => {
    try {
      if (!verification.pdfPath) {
        showToast("PDF is still generating. Please try again.", "error");
        return;
      }
      
      let endpoint;
      if (role === "superadmin") {
        endpoint = `${apiBaseUrl}/superadmin/kyc-verifications/${verification.id}/download`;
      } else if (role === "admin") {
        endpoint = `${apiBaseUrl}/admin/kyc-verifications/${verification.id}/download`;
      } else {
        endpoint = `${apiBaseUrl}/orguser/kyc/${verification.type}/${verification.id}/download`;
      }
      
      const token = authService.getAuthHeader();
      const response = await fetch(endpoint, { headers: token });
      const data = await response.json();
      
      const url = data?.data?.url || data?.url || data?.downloadUrl;
      if (url) {
        window.open(url, "_blank");
        showToast("Download started", "success");
      } else {
        showToast("Download URL not available", "error");
      }
    } catch (err) {
      console.error("Failed to download KYC verification report", err);
      showToast("Failed to download report", "error");
    }
  };

  const handleExportToExcel = () => {
    if (verifications.length === 0) {
      showToast("No data to export", "error");
      return;
    }

    // Prepare data for Excel
    const excelData = verifications.map((verification, index) => ({
      "S.No": index + 1,
      "Verification ID": verification.id,
      "Full Name": verification.fullName || "N/A",
      "Type": verification.type?.replace(/_/g, ' ') || "N/A",
      "Status": verification.status || "N/A",
      "Organization Name": verification.organizationName || "N/A",
      "Performed By": verification.performedByName || "N/A",
      "PAN Number": verification.panNumber || "N/A",
      "Aadhaar Number": verification.aadhaarNumber ? `****${verification.aadhaarNumber.slice(-4)}` : "N/A",
      "DL Number": verification.dlNumber || "N/A",
      "Cost": verification.cost || 0,
      "Date & Time": verification.createdAt
        ? new Date(verification.createdAt).toLocaleString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          })
        : "-"
    }));

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    
    // Set column widths
    const columnWidths = [
      { wch: 8 },  // S.No
      { wch: 40 }, // Verification ID
      { wch: 25 }, // Full Name
      { wch: 20 }, // Type
      { wch: 12 }, // Status
      { wch: 30 }, // Organization Name
      { wch: 25 }, // Performed By
      { wch: 15 }, // PAN Number
      { wch: 15 }, // Aadhaar Number
      { wch: 15 }, // DL Number
      { wch: 12 }, // Cost
      { wch: 22 }  // Date & Time
    ];
    worksheet['!cols'] = columnWidths;

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "KYC Verifications");

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `KYC_Verifications_${role}_${timestamp}.xlsx`;

    // Save file
    XLSX.writeFile(workbook, filename);
    showToast("Excel file downloaded successfully", "success");
  };

  return (
    <div className="kyc-verifications-page">
      {/* TOAST */}
      {toast.show && (
        <div className={`toast-notification ${toast.type === "success" ? "toast-success" : "toast-error"}`}>
          <span>{toast.message}</span>
        </div>
      )}

      <div className="page-header">
        <div>
          <h1>KYC Verification Reports</h1>
          <p>View KYC verification reports across all services</p>
        </div>
        <div className="header-actions">
          {/* Organization Selector - Only for Admin */}
          {role === "admin" && organizations.length > 0 && (
            <div className="header-org-selector">
              <select
                className="filter-select org-select"
                value={selectedOrgId}
                onChange={(e) => {
                  setSelectedOrgId(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All Organizations</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.organizationName}
                  </option>
                ))}
              </select>
            </div>
          )}
          {/* Export to Excel Button */}
          <button 
            className="btn-export-excel"
            onClick={handleExportToExcel}
            disabled={verifications.length === 0}
            title="Export to Excel"
          >
            <FaFileExcel /> Export to Excel
          </button>
        </div>
      </div>

      {/* Search and Filter Row */}
      <div className="search-filter-row">
        {/* Search Bar */}
        <div className="search-wrapper">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="search-input"
            placeholder="Search by name, PAN, Aadhaar, DL number..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        {/* Filters Group */}
        <div className="filters-group">
          {/* Type Filter */}
          <select
            className="filter-select type-filter"
            value={selectedType}
            onChange={(e) => {
              setSelectedType(e.target.value);
              setCurrentPage(1);
            }}
          >
            {KYC_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>

          {/* Date Range Filter */}
          <div className="date-filter">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setCurrentPage(1);
              }}
              className="date-input"
              placeholder="From Date"
            />
            <span className="date-separator">to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setCurrentPage(1);
              }}
              className="date-input"
              placeholder="To Date"
            />
          </div>
        </div>
      </div>

      <div className="table-container">
        {loading && (
          <div className="kyc-loading-message">Loading verifications...</div>
        )}
        {!loading && verifications.length === 0 && (
          <div className="empty-state">
            <p>No verifications found</p>
          </div>
        )}
        {!loading && verifications.length > 0 && (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Verification ID</th>
                  <th>Full Name</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Organization Name</th>
                  <th>Performed By</th>
                  <th>Cost</th>
                  <th>Date & Time</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {verifications.map((verification) => (
                  <tr key={verification.id}>
                    <td className="verification-id">{verification.id.substring(0, 8)}...</td>
                    <td>{verification.fullName || "N/A"}</td>
                    <td>
                      <span className="type-badge">
                        {verification.type?.replace(/_/g, ' ') || "N/A"}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge status-${verification.status?.toLowerCase()}`}>
                        {verification.status || "N/A"}
                      </span>
                    </td>
                    <td>{verification.organizationName || "N/A"}</td>
                    <td>{verification.performedByName || "N/A"}</td>
                    <td>{verification.cost || 0}</td>
                    <td>
                      {verification.createdAt
                        ? new Date(verification.createdAt).toLocaleString('en-IN', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })
                        : "-"}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon-view"
                          onClick={() => handleViewPdf(verification)}
                          title="View PDF Report"
                          disabled={!verification.pdfPath}
                        >
                          <FaEye />
                        </button>
                        <button
                          className="btn-icon-download"
                          onClick={() => handleDownload(verification)}
                          title="Download PDF"
                          disabled={!verification.pdfPath}
                        >
                          <FaDownload />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination-container">
                <div className="pagination-info">
                  Showing {(currentPage - 1) * verificationsPerPage + 1} to{" "}
                  {Math.min(currentPage * verificationsPerPage, totalRecords)} of {totalRecords}{" "}
                  records
                </div>
                <div className="pagination-buttons">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                    className="pagination-btn"
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }).map((_, i) => {
                    const pageNum = i + 1;
                    // Show first, last, current, and adjacent pages
                    if (
                      pageNum === 1 ||
                      pageNum === totalPages ||
                      (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNum}
                          className={`pagination-btn ${currentPage === pageNum ? "active" : ""}`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                      return <span key={pageNum}>...</span>;
                    }
                    return null;
                  })}
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                    className="pagination-btn"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

KycVerificationManagement.propTypes = {
  role: PropTypes.oneOf(["superadmin", "admin", "orguser"]),
  authService: PropTypes.shape({
    getAuthHeader: PropTypes.func.isRequired,
  }).isRequired,
  apiBaseUrl: PropTypes.string,
};

KycVerificationManagement.defaultProps = {
  role: "orguser",
  apiBaseUrl: process.env.REACT_APP_API_URL,
};

